import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CopyFeedbackIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  StatusBanner,
  useToast,
} from '@shared/ui';
import { useAuth } from '@entities/auth';
import { useApiServices } from '@shared/lib/use-api-services';
import { mapPasswordApiError, passwordErrorMessage } from '@entities/auth';
import { useSecondFactorAction } from '@features/security';
import { ApiError } from '@shared/api/errors/errors';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';

interface ApiTokenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApiTokenModal({ open, onOpenChange }: ApiTokenModalProps) {
  const { t, i18n } = useTranslation('cabinet');
  const { toast } = useToast();
  const { copy, isCopied } = useCopyToClipboard();
  const { user, refreshUser } = useAuth();
  const services = useApiServices();
  const { run } = useSecondFactorAction();
  const [code, setCode] = useState('');
  const [issuedToken, setIssuedToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const needsCode = Boolean(user?.twoFactorEnabled);
  const revoked = user?.apiToken == null && Boolean(user?.withdrawalsLockedUntil);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCode('');
      setIssuedToken('');
      setError('');
    }
    onOpenChange(next);
  };

  const issue = async () => {
    setIsLoading(true);
    setError('');

    try {
      const outcome = await run({
        purpose: 'api_token.regenerate',
        required: needsCode,
        request: async (factor) => {
          try {
            return await services.auth.refreshApiToken(factor ? { secondFactor: factor } : {});
          } catch (err: unknown) {
            const failure = mapPasswordApiError(err);
            if (failure?.code === 'OTP_INVALID' || failure?.code === 'OTP_REQUIRED') {
              throw new ApiError('otp', 400, {
                errors: [{ code: failure.code, methods: ['totp', 'backup'], purpose: 'api_token.regenerate' }],
              });
            }
            throw err;
          }
        },
      });
      if (!outcome.ok) {
        return;
      }
      const response = outcome.data;
      setIssuedToken(response.apiToken);
      await refreshUser();
      toast(t('apiToken.issued'), 'success');
    } catch (err: unknown) {
      const failure = mapPasswordApiError(err);
      if (failure?.code === 'OTP_INVALID') {
        setError(t('recovery.otpInvalid', { ns: 'auth' }));
        return;
      }
      const message = failure ? passwordErrorMessage(failure, t, i18n.language) : null;
      setError(message || t('apiToken.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('apiToken.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {revoked && (
            <StatusBanner variant="pending">{t('apiToken.revoked')}</StatusBanner>
          )}

          {issuedToken ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('apiToken.issuedHint')}</p>
              <p className="break-all rounded-xl bg-secondary px-3 py-3 font-mono text-sm">{issuedToken}</p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => void copy(issuedToken, t('apiToken.copied'), 'api-token')}
              >
                <CopyFeedbackIcon copied={isCopied('api-token')} className="mr-2 h-4 w-4" />
                {t('apiToken.copy')}
              </Button>
            </div>
          ) : (
            <>
              <p className="break-all font-mono text-sm text-foreground">
                {user?.apiToken || t('apiToken.empty')}
              </p>
              {needsCode && (
                <div className="space-y-2">
                  <Label htmlFor="api-token-code">{t('apiToken.code')}</Label>
                  <Input
                    id="api-token-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    disabled={isLoading}
                    error={error}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </div>
              )}
              {!needsCode && error && <p className="text-sm text-destructive">{error}</p>}
            </>
          )}
        </div>

        <DialogFooter>
          {!issuedToken && (
            <Button type="button" loading={isLoading} onClick={() => void issue()}>
              {user?.apiToken ? t('apiToken.reissue') : t('apiToken.issue')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
